import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTopClientBalanceMutation } from '@app/api/ClientPayments';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import {
  EDIT_CLIENT_PATH,
  FRONT_WARNING,
  LEAD_CARD_PATH,
  SALES_FUNNELS_LEAD_CARD_PATH,
  SUCCESS,
} from '@app/constants';
import {
  useClientsAccessRights,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NClients } from '@app/types';

const EDIT_CLIENT_TAB_KEY = '2';
const VISIT_HISTORY_TAB_KEY = '3';

export const useLeadCardMenuActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const { hasUserRightToEdit } = useClientsAccessRights();
  const { userAccessRights } = useNewUserAccessRights();
  const {
    isGoToLeadActionEnabled,
    isUserDetailsLoading,
    knownClientId,
    leadCardData,
    leadDetails,
    openLeadCardPage,
    refetchLeadCard,
    refetchLeadHistory,
    selectedBranchId,
    selectedLeadId,
  } = useLeadCardContext();

  const [isPaidModalOpen, setIsPaidModalOpen] = useState(false);
  const [isPaidModalLoading, setIsPaidModalLoading] = useState(false);
  const [isRefundWindowOpen, setIsRefundWindowOpen] = useState(false);
  const [client, setClient] = useState<NClients.ClientForView | null>(null);

  useEffect(() => {
    setClient(leadCardData?.ClientView ?? null);
  }, [leadCardData?.ClientView]);

  // Клиент, привязанный к лиду в ответе GetLeadCard, может отличаться от
  // клиента, уже открытого на странице/в модалке, куда встроена карточка
  // лида (объединение карточек, общий диалог на нескольких клиентов и т.п.).
  // Когда родитель явно знает своего клиента (knownClientId), доверяем ему,
  // а не значению из ответа лида — иначе действия карточки лида (история
  // визитов, баланс, возврат) уйдут не по тому клиенту.
  const clientId = useMemo(
    () =>
      knownClientId ??
      leadCardData?.ClientId ??
      leadCardData?.ClientView.Id ??
      null,
    [knownClientId, leadCardData?.ClientId, leadCardData?.ClientView.Id]
  );

  const clientPath = useMemo(() => {
    if (clientId === null) return null;

    const searchParams = new URLSearchParams({
      id: String(clientId),
    });

    if (selectedLeadId !== null) {
      searchParams.set('leadId', String(selectedLeadId));
    }

    return `${EDIT_CLIENT_PATH}?${searchParams.toString()}`;
  }, [clientId, selectedLeadId]);

  const isLeadMode =
    location.pathname.includes(LEAD_CARD_PATH) ||
    location.pathname.includes(SALES_FUNNELS_LEAD_CARD_PATH);
  const isClientActionAvailable = clientId !== null;
  const actionBranchId = selectedBranchId ?? activeBranchId;
  const hasUserRightToTopClientBalance =
    userAccessRights.SalesRegister.IsTopClientBalance;
  const hasUserRightToReturnMoney =
    userAccessRights.SalesRegister.IsReturnMoney;
  const hasUserRightToNavigateToLead =
    isGoToLeadActionEnabled && userAccessRights.Leads.IsOn;

  const [topClientBalance] = useTopClientBalanceMutation();

  const goToEdit = useCallback(() => {
    if (clientPath === null) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    navigate(`${clientPath}&key=${EDIT_CLIENT_TAB_KEY}`);
  }, [clientPath, navigate, showNotification]);

  const goToHistory = useCallback(() => {
    if (clientPath === null) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    navigate(
      `${clientPath}&key=${VISIT_HISTORY_TAB_KEY}${
        isLeadMode ? '&from=leads' : ''
      }`
    );
  }, [clientPath, isLeadMode, navigate, showNotification]);

  const openBalanceModal = useCallback(() => {
    if (clientId === null) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    setIsPaidModalOpen(true);
  }, [clientId, showNotification]);

  const openRefundWindow = useCallback(() => {
    if (clientId === null) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    setIsRefundWindowOpen(true);
  }, [clientId, showNotification]);

  const handleTopClientBalance = useCallback(
    async ({
      Amount,
      Comment,
      PaymentAccountId,
      form,
    }: NClients.HandleTopClientBalance) => {
      if (clientId === null) {
        showNotification('Идентификатор клиента не найден', FRONT_WARNING);
        return;
      }

      if (!actionBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        setIsPaidModalLoading(true);

        const { Balance } = await topClientBalance({
          ActiveBranchId: actionBranchId,
          data: {
            Amount: Number(Amount.replace(/\s+/g, '')),
            Comment,
            PaymentAccountId,
            ClientId: clientId,
          },
        }).unwrap();

        setClient((prev) =>
          prev
            ? {
                ...prev,
                Balance: Balance ?? prev.Balance,
              }
            : prev
        );
        refetchLeadCard();
        void refetchLeadHistory();
        showNotification('Баланс успешно пополнен', SUCCESS);
        form.resetFields();
      } catch (error) {
        showNotification(error);
      } finally {
        setIsPaidModalLoading(false);
        setIsPaidModalOpen(false);
      }
    },
    [
      actionBranchId,
      clientId,
      refetchLeadCard,
      refetchLeadHistory,
      showNotification,
      topClientBalance,
    ]
  );

  const updateClientAfterRefund = useCallback(
    (nextClient: NClients.ClientForView) => {
      setClient(nextClient);
      refetchLeadCard();
    },
    [refetchLeadCard]
  );

  const sidebarInfo = useMemo(
    () => ({
      userName: leadDetails?.profile.name ?? '',
      avatar: leadDetails?.profile.avatarBase64 ?? '',
    }),
    [leadDetails?.profile.avatarBase64, leadDetails?.profile.name]
  );

  return {
    actionBranchId,
    client,
    goToEdit,
    goToHistory,
    handleTopClientBalance,
    hasUserRightToEdit,
    hasUserRightToNavigateToLead,
    hasUserRightToReturnMoney,
    hasUserRightToTopClientBalance,
    isClientActionAvailable,
    isPaidModalLoading,
    isPaidModalOpen,
    isRefundWindowOpen,
    isUserDetailsLoading,
    openBalanceModal,
    openLeadCardPage,
    openRefundWindow,
    refetchLeadHistory,
    setIsPaidModalOpen,
    setIsRefundWindowOpen,
    sidebarInfo,
    updateClientAfterRefund,
  };
};
